import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DataTable } from "@/components/common/DataTable";
import { ListToolbar } from "@/components/common/ListToolbar";
import { DataPagination } from "@/components/common/DataPagination";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import { useUrlState } from "@/lib/urlState";
import { queryKeys } from "@/lib/queryKeys";
import { exportRowsAsCsv } from "@/lib/csv";

interface EmailTemplate {
  id: string;
  name: string;
  type: string;
  subject: string;
  body: string;
  created_at: string;
  updated_at: string;
}

const EmailTemplateTable = () => {
  const { toast } = useToast();
  const qc = useQueryClient();
  const url = useUrlState();
  const search = url.get("q") ?? "";
  const [pendingDelete, setPendingDelete] = useState<EmailTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);

  const paginated = usePaginatedQuery<EmailTemplate>({
    queryKey: queryKeys.emailTemplates.list({
      page: 0,
      pageSize: 25,
      filters: { search },
    }),
    table: "email_templates",
    select: "id,name,type,subject,body,created_at,updated_at",
    syncToUrl: true,
    orderBy: { column: "updated_at", ascending: false },
    filters: (q) =>
      search
        ? q.or(`name.ilike.%${search}%,subject.ilike.%${search}%,type.ilike.%${search}%`)
        : q,
  });

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const { error } = await supabase
        .from("email_templates")
        .delete()
        .eq("id", pendingDelete.id);
      if (error) throw error;
      toast({ title: "Template deleted", description: pendingDelete.name });
      qc.invalidateQueries({ queryKey: ["emailTemplates"] });
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Error",
        description: err?.message ?? "Failed to delete template",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  };

  const columns = useMemo<ColumnDef<EmailTemplate, unknown>[]>(
    () => [
      { accessorKey: "name", header: "Name" },
      {
        accessorKey: "type",
        header: "Type",
        cell: ({ row }) => (
          <Badge variant="outline" className="capitalize">
            {row.original.type}
          </Badge>
        ),
      },
      {
        accessorKey: "subject",
        header: "Subject",
        cell: ({ row }) => (
          <span className="line-clamp-1">{row.original.subject}</span>
        ),
      },
      {
        accessorKey: "updated_at",
        header: "Updated",
        cell: ({ row }) =>
          new Date(row.original.updated_at).toLocaleDateString(),
      },
      {
        id: "actions",
        header: () => <div className="text-right">Actions</div>,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-red-700 hover:bg-red-50"
              onClick={() => setPendingDelete(row.original)}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-3">
      <ListToolbar
        search={search}
        onSearchChange={(v) => url.set({ q: v || null, page: 0 })}
        searchPlaceholder="Search templates by name, subject, or type…"
        onExportCsv={() =>
          exportRowsAsCsv(
            "email-templates.csv",
            paginated.rows as unknown as Record<string, unknown>[],
          )
        }
      />

      <DataTable
        columns={columns}
        data={paginated.rows}
        isLoading={paginated.isLoading}
        isError={paginated.isError}
        onRetry={paginated.refetch}
        emptyTitle="No templates found"
        emptyDescription="Create your first email template using the form."
        getRowId={(row) => row.id}
      />

      <DataPagination
        page={paginated.page}
        pageSize={paginated.pageSize}
        total={paginated.total}
        pageCount={paginated.pageCount}
        onPageChange={paginated.setPage}
        onPageSizeChange={paginated.setPageSize}
        isFetching={paginated.isFetching}
      />

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete email template?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{pendingDelete?.name}</strong>.
              Any campaigns referencing it will need to be reassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EmailTemplateTable;
